import { useApiQuery } from '../useApiQuery';
import { apiGet, operationsClient } from '@/lib/api';
import { auditLogEndpoints } from '@/lib/api/endpoints';
import { auditLogKeys } from '@/lib/queryKeys';
import type { AuditLogListParams, AuditLogListResponse } from '@/types/auditLog';
import { useHasPermission } from '@/hooks/usePermissions';

/**
 * Fetches the paginated audit log from the operations microservice.
 * Audit logs are always served by operations — there is no monolith fallback.
 */
export function useAuditLogs(params: AuditLogListParams = {}, options: { enabled?: boolean } = {}) {
  const canViewAudit = useHasPermission('audit:view');
  // Map userId alias to actorId for the backend
  const backendParams = {
    ...params,
    actorId: params.actorId ?? params.userId,
  };

  const query = useApiQuery<AuditLogListResponse>({
    queryKey: auditLogKeys.list(params),
    queryFn: () =>
      apiGet<AuditLogListResponse>(operationsClient, auditLogEndpoints.list(), {
        params: backendParams,
      }),
    enabled: canViewAudit && (options.enabled ?? true),
    showErrorToast: false,
  });
  return canViewAudit ? query : { ...query, data: undefined };
}

/**
 * Fetches audit log entries scoped to a specific entity (e.g. a contact or invoice).
 */
export function useEntityAuditLogs(
  entityType: string,
  entityId: string,
  params: AuditLogListParams = {},
  options: { enabled?: boolean } = {},
) {
  const canViewAudit = useHasPermission('audit:view');
  const { enabled = true } = options;
  const query = useApiQuery<AuditLogListResponse>({
    queryKey: auditLogKeys.byEntity(entityType, entityId),
    queryFn: () =>
      apiGet<AuditLogListResponse>(
        operationsClient,
        auditLogEndpoints.byEntity(entityType, entityId),
        { params },
      ),
    enabled: canViewAudit && enabled && Boolean(entityType) && Boolean(entityId),
    showErrorToast: false,
    // Audit rows are append-only and each row's diff is immutable once
    // written, so we cache forever within a session. New rows appear via
    // explicit invalidation when mutations happen on the entity (or on
    // the next page load). No polling, no focus refetch.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  return canViewAudit ? query : { ...query, data: undefined };
}

/**
 * Fetches audit stats for a specific entity.
 */
export function useEntityAuditStats(entityType: string, entityId: string) {
  const canViewAudit = useHasPermission('audit:view');
  const query = useApiQuery<{ action: string; count: number; lastOccurrence: string }[]>({
    queryKey: [...auditLogKeys.byEntity(entityType, entityId), 'stats'],
    queryFn: () => apiGet(operationsClient, auditLogEndpoints.stats(entityType, entityId)),
    enabled: canViewAudit && Boolean(entityType) && Boolean(entityId),
    showErrorToast: false,
  });
  return canViewAudit ? query : { ...query, data: undefined };
}
